import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Crown, Heart, Skull } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TwoHeartsCanvas } from "@/components/twohearts/TwoHeartsGame";
import { skinColors } from "@/components/twohearts/THExtras";
import { UserAvatar } from "@/components/UserBits";

const Bar = ({ v, me }) => (
  <div className="absolute bottom-4 inset-x-3 z-10 flex items-center gap-2 rounded-full bg-black/40 backdrop-blur px-3 py-1.5 text-white text-sm font-bold" data-testid="thduel-live">
    <span className="flex-1">Du <b className="tabular-nums" data-testid="thduel-me-score">{me}</b></span>
    <span className="text-white/50">vs</span>
    <span className="flex-1 text-right truncate">{v.opp.user?.name?.split(" ")[0]} <b className="tabular-nums" data-testid="thduel-opp-score">{v.opp.score}</b> {!v.opp.alive && <Skull className="w-3.5 h-3.5 inline text-white/60" />}</span>
  </div>
);

function Duel({ id }) {
  const [v, setV] = useState(null);
  const [skin, setSkin] = useState(null);
  const [now, setNow] = useState(Date.now());
  const score = useRef(0);
  const [me, setMe] = useState(0);
  const [dead, setDead] = useState(false);
  const startAt = useRef(null);
  if (v?.starts_in != null && startAt.current == null) startAt.current = Date.now() + v.starts_in * 1000;
  const load = () => api.get(`/twohearts/duel/${id}`).then(({ data }) => setV(data)).catch((e) => setV({ err: formatErr(e) }));
  useEffect(() => { load(); api.get("/twohearts").then(({ data }) => setSkin(skinColors(data))).catch(() => setSkin({})); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const playing = v?.status === "live" && v.me.alive && !dead && startAt.current != null && now >= startAt.current;
  useEffect(() => {
    const iv = setInterval(async () => {
      setNow(Date.now());
      if (playing) {
        try { const { data } = await api.post(`/twohearts/duel/${id}/progress`, { score: score.current, alive: true }); setV(data); } catch { /* retry next tick */ }
      } else if (!v || ["pending", "live"].includes(v.status)) load();
    }, 1000);
    return () => clearInterval(iv);
  }, [playing, v?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const over = async ({ score: s }) => {
    setDead(true);
    try { const { data } = await api.post(`/twohearts/duel/${id}/progress`, { score: s, alive: false }); setV(data); } catch (e) { toast.error(formatErr(e)); }
  };
  const act = async (a) => { try { const { data } = await api.post(`/twohearts/duel/${id}/${a}`); if (a === "accept") setV(data); else load(); } catch (e) { toast.error(formatErr(e)); } };
  if (!v || !skin) return <div className="min-h-[100dvh] bg-[#2a0b2e]" />;
  if (v.err) return <Center><p data-testid="thduel-error">{v.err}</p><Back /></Center>;
  const opp = v.opp.user?.name?.split(" ")[0] || "Dein Freund";
  if (v.status === "pending") return (
    <Center>
      <UserAvatar name={v.opp.user?.name} picture={v.opp.user?.picture} size="w-20 h-20" noStory />
      {v.mine ? <p className="text-lg font-bold" data-testid="thduel-waiting">Warte auf {opp} … 💞</p> : (
        <>
          <p className="text-lg font-bold">{opp} fordert dich zum Herzen-Duell heraus!</p>
          <div className="flex gap-2">
            <button onClick={() => act("accept")} data-testid="thduel-accept" className="h-12 px-6 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 font-black">Annehmen</button>
            <button onClick={() => act("decline")} data-testid="thduel-decline" className="h-12 px-5 rounded-full bg-white/10 font-bold">Ablehnen</button>
          </div>
        </>
      )}
      <Back />
    </Center>
  );
  if (["declined", "expired"].includes(v.status)) return <Center><p data-testid="thduel-closed">{v.status === "declined" ? (v.mine ? `${opp} hat abgelehnt.` : "Du hast das Duell abgelehnt.") : "Das Duell ist abgelaufen."}</p><Back /></Center>;
  const left = Math.min(5, Math.ceil(((startAt.current ?? now) - now) / 1000));
  return (
    <div className="fixed inset-0 z-[80] bg-[#2a0b2e]" data-testid="thduel-play">
      {v.status === "live" && left <= 0 && !dead && v.me.alive && <TwoHeartsCanvas seed={v.seed} skin={skin} onScore={(s) => { score.current = s; setMe(s); }} onOver={over} />}
      <Bar v={v} me={Math.max(me, v.me.score)} />
      {v.status === "live" && left > 0 && <div className="absolute inset-0 grid place-items-center text-white"><p className="font-display text-8xl font-black th-pop" key={left} data-testid="thduel-countdown">{left}</p></div>}
      {v.status === "live" && (dead || !v.me.alive) && <Center overlay><Heart className="w-10 h-10 text-pink-400" fill="currentColor" /><p className="text-lg font-bold" data-testid="thduel-wait-opp">Du hast {Math.max(me, v.me.score)} Punkte. {opp} spielt noch … ({v.opp.score})</p></Center>}
      {v.status === "done" && (
        <Center overlay>
          {v.won ? <Crown className="w-14 h-14 text-amber-300" /> : <Heart className="w-12 h-12 text-pink-400" fill="currentColor" />}
          <p className="font-display text-4xl font-black" data-testid="thduel-result">{v.won ? "Gewonnen! +5" : v.winner ? `${opp} gewinnt` : "Unentschieden"}</p>
          <p className="text-white/80">Du {v.me.score} · {opp} {v.opp.score}</p>
          <Back />
        </Center>
      )}
    </div>
  );
}

const Center = ({ children, overlay }) => (
  <div className={`${overlay ? "absolute z-20 bg-black/55 backdrop-blur-sm" : "min-h-[100dvh] bg-[#2a0b2e]"} inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center text-white`}>{children}</div>
);
const Back = () => <Link to="/zwei-herzen" data-testid="thduel-back" className="h-11 px-6 rounded-full bg-white/10 font-bold grid place-items-center">Zurück zum Spiel</Link>;

export default function TwoHeartsDuel() {
  const { id } = useParams();
  const { user } = useAuth();
  if (user === null) return <div className="min-h-[100dvh] bg-[#2a0b2e]" />;
  if (!user) { sessionStorage.setItem("after_auth", `/zwei-herzen/duell/${id}`); return <Navigate to="/login" replace />; }
  return <Duel id={id} />;
}
